import React, { useEffect, useRef, useState, Fragment } from 'react';
import { toast } from 'react-toastify';
import { useHistory } from 'react-router-dom';
import api from '../../config/axios';
import Breadcrumb from '../common/breadcrumb';
import { useStateValue } from '../../store';
// Mismos helpers de new-client: URL de Maps → coordenadas y teléfono VE
import {
    extractCoords,
    detectShortMapsUrl,
    normalizePhoneVE,
} from '../clients/parse-whatsapp';

// R3: zonas y tarifas precargadas — igual que la calculadora pública
const ZONAS = [
    { zona: 1, nombre: 'Zona 1', tarifa: 6000 },
    { zona: 2, nombre: 'Zona 2', tarifa: 8000 },
    { zona: 3, nombre: 'Zona 3', tarifa: 12000 },
];

const FRANQUICIA_SC = '5e95f51f-5b66-4c58-b2af-7a54e8b1df1e';

const NewEnvio = () => {
    const [{ auth }] = useStateValue();
    const history = useHistory();
    const [businesses, setBusinesses] = useState([]);
    const [paymentMethods, setPaymentMethods] = useState([]);
    const [submitting, setSubmitting] = useState(false);

    // URL de Google Maps → coordenadas (mismo flujo que new-client)
    const [mapsUrlInput, setMapsUrlInput] = useState('');
    const mapsUrlTimer = useRef(null);
    const [mapsLoading, setMapsLoading] = useState(false);

    // Registro de negocio nuevo inline (POST /businesses desde el dashboard)
    const [showNewBiz, setShowNewBiz] = useState(false);
    const [creatingBiz, setCreatingBiz] = useState(false);
    const [bizForm, setBizForm] = useState({
        name: '',
        phone: '',
        contactName: '',
        addressStreet1: '',
        addressLat: '',
        addressLng: '',
    });
    const [bizMapsUrlInput, setBizMapsUrlInput] = useState('');
    const bizMapsTimer = useRef(null);
    const [bizMapsLoading, setBizMapsLoading] = useState(false);

    const franchiseId =
        (auth.user && auth.user.franchiseId) || FRANQUICIA_SC;

    const [form, setForm] = useState({
        origen: 'business', // 'business' | 'adhoc'
        businessId: '',
        businessName: '',
        receiverName: '',
        receiverPhone: '',
        receiverStreet1: '',
        receiverStreet2: '',
        receiverLatitude: '',
        receiverLongitude: '',
        zona: '',
        paymentMethodId: '',
        descripcionPaquete: '',
        valorPaquete: '',
        tipoCobro: 'solo_envio',
        vueltos: '',
    });

    useEffect(() => {
        fetchBusinesses();
        fetchPaymentMethods();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const fetchBusinesses = async () => {
        try {
            const { data } = await api.get('/businesses');
            setBusinesses(data.data || []);
        } catch (err) {
            toast.error('Error al cargar los negocios');
        }
    };

    const fetchPaymentMethods = async () => {
        try {
            const { data } = await api.get(
                `/franchise/${franchiseId}/payment_method`,
            );
            setPaymentMethods(data.data || []);
        } catch (err) {
            toast.error('Error al cargar las formas de pago');
        }
    };

    const tarifaSeleccionada = ZONAS.find((z) => z.zona === Number(form.zona));

    const handleChange = (field, value) => {
        setForm((prev) => ({ ...prev, [field]: value }));
    };

    // decodeURIComponent falla si el texto tiene un % suelto — no rompe el flujo
    const safeUrl = (text) => {
        try {
            return decodeURIComponent(text);
        } catch (e) {
            return text;
        }
    };

    /**
     * Resuelve coordenadas desde lo pegado en un campo URL de Google Maps:
     * - Coordenadas sueltas "7.7665, -72.2287" → directas
     * - URL larga de Maps → coordenadas en el propio texto (@lat,lng / !3d!4d / ?q=)
     * - URL corta (goo.gl) → se expande vía /misc/expand-url y se parsea
     * Devuelve {lat, lng} o null. La usan el receptor y el negocio nuevo.
     */
    const resolveMapsCoords = async (value) => {
        const trimmed = (value || '').trim();
        if (!trimmed) return null;

        // Coordenadas sueltas
        const direct = extractCoords(trimmed);
        if (direct.lat !== null) return direct;

        // URL larga: las coordenadas vienen en el propio texto
        const fromLong = extractCoords(decodeURIComponent(safeUrl(trimmed)));
        if (fromLong.lat !== null) return fromLong;

        // URL corta: expandir vía API
        const short = detectShortMapsUrl(trimmed);
        if (short) {
            const response = await api.get('/misc/expand-url', {
                // _t evita que el navegador devuelva un 304 sin cuerpo
                params: { url: short, _t: Date.now() },
            });
            const expandedUrl =
                response.data && response.data.data && response.data.data.url;
            if (expandedUrl) {
                const coords = extractCoords(decodeURIComponent(expandedUrl));
                if (coords.lat !== null) return coords;
                toast.warn('El link no trae coordenadas — pégalas o escríbelas a mano');
                return null;
            }
            toast.warn('El link no trae coordenadas — pégalas o escríbelas a mano');
            return null;
        }
        return null;
    };

    const setCoords = (coords) => {
        setForm((prev) => ({
            ...prev,
            receiverLatitude: coords.lat,
            receiverLongitude: coords.lng,
        }));
        toast.success('📍 Ubicación detectada');
    };

    const setBizCoords = (coords) => {
        setBizForm((prev) => ({
            ...prev,
            addressLat: coords.lat,
            addressLng: coords.lng,
        }));
        toast.success('📍 Ubicación del negocio detectada');
    };

    const handleMapsUrlChange = (e) => {
        const value = e.target.value;
        setMapsUrlInput(value);
        clearTimeout(mapsUrlTimer.current);
        // Debounce: al pegar el link, las coordenadas se llenan ~1s después
        mapsUrlTimer.current = setTimeout(async () => {
            setMapsLoading(true);
            try {
                const coords = await resolveMapsCoords(value).catch(() => {
                    toast.warn('No pude resolver el link de Maps — pégalas o escríbelas a mano');
                    return null;
                });
                if (coords) setCoords(coords);
            } finally {
                setMapsLoading(false);
            }
        }, 1000);
    };

    const handleBizMapsUrlChange = (e) => {
        const value = e.target.value;
        setBizMapsUrlInput(value);
        clearTimeout(bizMapsTimer.current);
        bizMapsTimer.current = setTimeout(async () => {
            setBizMapsLoading(true);
            try {
                const coords = await resolveMapsCoords(value).catch(() => {
                    toast.warn('No pude resolver el link de Maps — pégalas o escríbelas a mano');
                    return null;
                });
                if (coords) setBizCoords(coords);
            } finally {
                setBizMapsLoading(false);
            }
        }, 1000);
    };

    const handleBizChange = (field, value) => {
        setBizForm((prev) => ({ ...prev, [field]: value }));
    };

    /**
     * Registra el negocio nuevo (POST /businesses), refresca la lista del
     * select y lo deja autoseleccionado como origen del envío.
     */
    const handleCreateBusiness = async () => {
        if (!bizForm.name.trim() || !bizForm.phone.trim()) {
            toast.warning('Completa nombre y teléfono del negocio');
            return;
        }
        const payload = {
            name: bizForm.name.trim(),
            phone: normalizePhoneVE(bizForm.phone) || bizForm.phone.trim(),
            contactName: bizForm.contactName.trim() || null,
            addressStreet1: bizForm.addressStreet1.trim() || null,
            addressLat: bizForm.addressLat || null,
            addressLng: bizForm.addressLng || null,
        };

        setCreatingBiz(true);
        try {
            const response = await api.post('/businesses', payload);
            const created =
                response.data && response.data.data ? response.data.data : null;
            await fetchBusinesses();
            if (created && created.id) {
                handleChange('businessId', String(created.id));
            }
            toast.success(`Negocio "${payload.name}" registrado`);
            setShowNewBiz(false);
            setBizMapsUrlInput('');
            setBizForm({
                name: '',
                phone: '',
                contactName: '',
                addressStreet1: '',
                addressLat: '',
                addressLng: '',
            });
        } catch (err) {
            const msg =
                err.response && err.response.data && err.response.data.errors
                    ? err.response.data.errors[0].msg
                    : 'Error al registrar el negocio';
            toast.error(typeof msg === 'string' ? msg : 'Error al registrar el negocio');
        } finally {
            setCreatingBiz(false);
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();

        if (!form.receiverName || !form.receiverPhone || !form.receiverStreet1) {
            toast.warning('Completa nombre, teléfono y dirección del receptor');
            return;
        }
        if (!form.zona) {
            toast.warning('Selecciona la zona del envío');
            return;
        }
        if (form.origen === 'business' && !form.businessId) {
            toast.warning('Selecciona un negocio o usa origen ad-hoc');
            return;
        }
        if (form.origen === 'adhoc' && !form.businessName.trim()) {
            toast.warning('Escribe el nombre del origen ad-hoc');
            return;
        }

        const payload = {
            receiverName: form.receiverName.trim(),
            // Normaliza "+58 414-7353821" / "0414-…" → 58414… (mismo new-client)
            receiverPhone:
                normalizePhoneVE(form.receiverPhone) || form.receiverPhone.trim(),
            receiverStreet1: form.receiverStreet1.trim(),
            // receiverStreet2: form.receiverStreet2.trim() || null, // campo comentado a petición de Romer
            receiverLatitude: form.receiverLatitude || null,
            receiverLongitude: form.receiverLongitude || null,
            zona: Number(form.zona),
            paymentMethodId: form.paymentMethodId || null,
            descripcionPaquete: form.descripcionPaquete.trim() || null,
            valorPaquete: form.valorPaquete || null,
            tipoCobro: form.tipoCobro || 'solo_envio',
            vueltos: form.vueltos || null,
        };
        if (form.origen === 'business') {
            payload.businessId = Number(form.businessId);
        } else {
            payload.businessName = form.businessName.trim();
        }

        setSubmitting(true);
        try {
            await api.post('/deliveryOrders', payload);
            toast.success('Envío registrado');
            history.push(`${process.env.PUBLIC_URL}/envios`);
        } catch (err) {
            const msg =
                err.response && err.response.data && err.response.data.errors
                    ? err.response.data.errors[0].msg
                    : 'Error al registrar el envío';
            toast.error(typeof msg === 'string' ? msg : 'Error al registrar el envío');
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <Fragment>
            <Breadcrumb title="Nuevo envío" />
            <div className="container-fluid">
                <div className="row">
                    <div className="col-sm-12">
                        <div className="card">
                            <div className="card-body">
                                <form onSubmit={handleSubmit}>
                                    {/* Origen */}
                                    <h6 className="mb-3">Origen (pickup)</h6>
                                    <div className="form-group">
                                        <label className="mr-3">Tipo de origen:</label>
                                        <div className="form-check form-check-inline">
                                            <input
                                                className="form-check-input"
                                                type="radio"
                                                name="origen"
                                                id="origen-business"
                                                value="business"
                                                checked={form.origen === 'business'}
                                                onChange={(e) =>
                                                    handleChange('origen', e.target.value)
                                                }
                                            />
                                            <label
                                                className="form-check-label"
                                                htmlFor="origen-business"
                                            >
                                                Negocio registrado
                                            </label>
                                        </div>
                                        <div className="form-check form-check-inline">
                                            <input
                                                className="form-check-input"
                                                type="radio"
                                                name="origen"
                                                id="origen-adhoc"
                                                value="adhoc"
                                                checked={form.origen === 'adhoc'}
                                                onChange={(e) =>
                                                    handleChange('origen', e.target.value)
                                                }
                                            />
                                            <label
                                                className="form-check-label"
                                                htmlFor="origen-adhoc"
                                            >
                                                Ad-hoc (sin registrar)
                                            </label>
                                        </div>
                                    </div>

                                    {form.origen === 'business' ? (
                                        <div className="form-group">
                                            <label>Negocio</label>
                                            <select
                                                className="form-control"
                                                value={form.businessId}
                                                onChange={(e) =>
                                                    handleChange('businessId', e.target.value)
                                                }
                                            >
                                                <option value="">Seleccionar...</option>
                                                {businesses
                                                    .filter((b) => b.status !== false)
                                                    .map((b) => (
                                                        <option key={b.id} value={b.id}>
                                                            {b.name}
                                                        </option>
                                                    ))}
                                            </select>
                                            <button
                                                type="button"
                                                className="btn btn-link btn-sm pl-0 mt-1"
                                                onClick={() => setShowNewBiz((v) => !v)}
                                            >
                                                {showNewBiz
                                                    ? 'Cerrar'
                                                    : '+ Registrar negocio nuevo'}
                                            </button>
                                            {showNewBiz && (
                                                <div
                                                    className="modal fade show"
                                                    style={{
                                                        display: 'block',
                                                        backgroundColor: 'rgba(0,0,0,0.5)',
                                                    }}
                                                    tabIndex="-1"
                                                >
                                                    <div className="modal-dialog modal-dialog-centered">
                                                        <div className="modal-content">
                                                            <div className="modal-header">
                                                                <h5 className="modal-title">
                                                                    Registrar negocio nuevo
                                                                </h5>
                                                                <button
                                                                    type="button"
                                                                    className="close"
                                                                    onClick={() =>
                                                                        setShowNewBiz(false)
                                                                    }
                                                                >
                                                                    <span>&times;</span>
                                                                </button>
                                                            </div>
                                                            <div className="modal-body">
                                                                <div className="form-row">
                                                                    <div className="form-group col-md-6">
                                                                        <label>Nombre *</label>
                                                                        <input
                                                                            type="text"
                                                                            className="form-control"
                                                                            placeholder="Ej: Farmacia Central"
                                                                            value={bizForm.name}
                                                                            onChange={(e) =>
                                                                                handleBizChange(
                                                                                    'name',
                                                                                    e.target.value,
                                                                                )
                                                                            }
                                                                        />
                                                                    </div>
                                                                    <div className="form-group col-md-6">
                                                                        <label>Teléfono *</label>
                                                                        <input
                                                                            type="text"
                                                                            className="form-control"
                                                                            placeholder="0414... / 0776..."
                                                                            value={bizForm.phone}
                                                                            onChange={(e) =>
                                                                                handleBizChange(
                                                                                    'phone',
                                                                                    e.target.value,
                                                                                )
                                                                            }
                                                                        />
                                                                    </div>
                                                                </div>
                                                                <div className="form-row">
                                                                    <div className="form-group col-md-6">
                                                                        <label>Persona de contacto</label>
                                                                        <input
                                                                            type="text"
                                                                            className="form-control"
                                                                            placeholder="Opcional"
                                                                            value={bizForm.contactName}
                                                                            onChange={(e) =>
                                                                                handleBizChange(
                                                                                    'contactName',
                                                                                    e.target.value,
                                                                                )
                                                                            }
                                                                        />
                                                                    </div>
                                                                    <div className="form-group col-md-6">
                                                                        <label>Dirección</label>
                                                                        <input
                                                                            type="text"
                                                                            className="form-control"
                                                                            placeholder="Calle / avenida del negocio"
                                                                            value={bizForm.addressStreet1}
                                                                            onChange={(e) =>
                                                                                handleBizChange(
                                                                                    'addressStreet1',
                                                                                    e.target.value,
                                                                                )
                                                                            }
                                                                        />
                                                                    </div>
                                                                </div>
                                                                <div className="form-group">
                                                                    <label>
                                                                        Ubicación (URL de Google Maps)
                                                                    </label>
                                                                    <input
                                                                        type="text"
                                                                        className="form-control"
                                                                        placeholder="Pega el link del negocio — detecta las coordenadas"
                                                                        value={bizMapsUrlInput}
                                                                        onChange={handleBizMapsUrlChange}
                                                                    />
                                                                    {bizMapsLoading && (
                                                                        <small className="form-text text-muted">
                                                                            Resolviendo link…
                                                                        </small>
                                                                    )}
                                                                </div>
                                                                <div className="form-row">
                                                                    <div className="form-group col-md-6">
                                                                        <label>Latitud</label>
                                                                        <input
                                                                            type="number"
                                                                            step="any"
                                                                            className="form-control"
                                                                            value={bizForm.addressLat}
                                                                            onChange={(e) =>
                                                                                handleBizChange(
                                                                                    'addressLat',
                                                                                    e.target.value,
                                                                                )
                                                                            }
                                                                        />
                                                                    </div>
                                                                    <div className="form-group col-md-6">
                                                                        <label>Longitud</label>
                                                                        <input
                                                                            type="number"
                                                                            step="any"
                                                                            className="form-control"
                                                                            value={bizForm.addressLng}
                                                                            onChange={(e) =>
                                                                                handleBizChange(
                                                                                    'addressLng',
                                                                                    e.target.value,
                                                                                )
                                                                            }
                                                                        />
                                                                    </div>
                                                                </div>
                                                            </div>
                                                            <div className="modal-footer">
                                                                <button
                                                                    type="button"
                                                                    className="btn btn-secondary"
                                                                    onClick={() =>
                                                                        setShowNewBiz(false)
                                                                    }
                                                                >
                                                                    Cancelar
                                                                </button>
                                                                <button
                                                                    type="button"
                                                                    className="btn btn-primary"
                                                                    disabled={creatingBiz}
                                                                    onClick={handleCreateBusiness}
                                                                >
                                                                    {creatingBiz
                                                                        ? 'Registrando...'
                                                                        : 'Guardar negocio'}
                                                                </button>
                                                            </div>
                                                        </div>
                                                    </div>
                                                </div>
                                            )}
                                        </div>
                                    ) : (
                                        <div className="form-group">
                                            <label>Nombre del origen</label>
                                            <input
                                                type="text"
                                                className="form-control"
                                                placeholder="Ej: McDonald's, particular..."
                                                value={form.businessName}
                                                onChange={(e) =>
                                                    handleChange('businessName', e.target.value)
                                                }
                                            />
                                        </div>
                                    )}

                                    <hr />

                                    {/* Receptor (denormalizado en la orden) */}
                                    <h6 className="mb-3">Receptor</h6>
                                    <div className="form-row">
                                        <div className="form-group col-md-6">
                                            <label>Nombre *</label>
                                            <input
                                                type="text"
                                                className="form-control"
                                                value={form.receiverName}
                                                onChange={(e) =>
                                                    handleChange(
                                                        'receiverName',
                                                        e.target.value,
                                                    )
                                                }
                                                required
                                            />
                                        </div>
                                        <div className="form-group col-md-6">
                                            <label>Teléfono *</label>
                                            <input
                                                type="text"
                                                className="form-control"
                                                placeholder="0414..."
                                                value={form.receiverPhone}
                                                onChange={(e) =>
                                                    handleChange(
                                                        'receiverPhone',
                                                        e.target.value,
                                                    )
                                                }
                                                required
                                            />
                                        </div>
                                    </div>
                                    <div className="form-group">
                                        <label>Dirección *</label>
                                        <input
                                            type="text"
                                            className="form-control"
                                            placeholder="Calle / avenida / edificio"
                                            value={form.receiverStreet1}
                                            onChange={(e) =>
                                                handleChange(
                                                    'receiverStreet1',
                                                    e.target.value,
                                                )
                                            }
                                            required
                                        />
                                    </div>
                                    {/* Campo comentado a petición de Romer (3-nov):
                                    <div className="form-group">
                                        <label>Referencia adicional</label>
                                        <input
                                            type="text"
                                            className="form-control"
                                            placeholder="Apto, casa, punto de referencia..."
                                            value={form.receiverStreet2}
                                            onChange={(e) =>
                                                handleChange(
                                                    'receiverStreet2',
                                                    e.target.value,
                                                )
                                            }
                                        />
                                    </div>
                                    */}
                                    <div className="form-group">
                                        <label>Ubicación (URL de Google Maps)</label>
                                        <input
                                            type="text"
                                            className="form-control"
                                            placeholder="Pega el link de Google Maps — detecta las coordenadas"
                                            value={mapsUrlInput}
                                            onChange={handleMapsUrlChange}
                                        />
                                        {mapsLoading && (
                                            <small className="form-text text-muted">
                                                Resolviendo link…
                                            </small>
                                        )}
                                    </div>
                                    <div className="form-row">
                                        <div className="form-group col-md-6">
                                            <label>Latitud</label>
                                            <input
                                                type="number"
                                                step="any"
                                                className="form-control"
                                                value={form.receiverLatitude}
                                                onChange={(e) =>
                                                    handleChange(
                                                        'receiverLatitude',
                                                        e.target.value,
                                                    )
                                                }
                                            />
                                        </div>
                                        <div className="form-group col-md-6">
                                            <label>Longitud</label>
                                            <input
                                                type="number"
                                                step="any"
                                                className="form-control"
                                                value={form.receiverLongitude}
                                                onChange={(e) =>
                                                    handleChange(
                                                        'receiverLongitude',
                                                        e.target.value,
                                                    )
                                                }
                                            />
                                        </div>
                                    </div>

                                    <hr />

                                    {/* Zona + tarifa + pago */}
                                    <h6 className="mb-3">Zona, tarifa y pago</h6>
                                    <div className="form-row">
                                        <div className="form-group col-md-4">
                                            <label>Zona *</label>
                                            <select
                                                className="form-control"
                                                value={form.zona}
                                                onChange={(e) =>
                                                    handleChange('zona', e.target.value)
                                                }
                                            >
                                                <option value="">Seleccionar...</option>
                                                {ZONAS.map((z) => (
                                                    <option key={z.zona} value={z.zona}>
                                                        {z.nombre} — ${' '}
                                                        {z.tarifa.toLocaleString('es-CO')}{' '}
                                                        COP
                                                    </option>
                                                ))}
                                            </select>
                                        </div>
                                        <div className="form-group col-md-4">
                                            <label>Tarifa</label>
                                            <input
                                                type="text"
                                                className="form-control"
                                                disabled
                                                value={
                                                    tarifaSeleccionada
                                                        ? `$ ${tarifaSeleccionada.tarifa.toLocaleString(
                                                              'es-CO',
                                                          )} COP`
                                                        : ''
                                                }
                                            />
                                        </div>
                                        <div className="form-group col-md-4">
                                            <label>Forma de pago</label>
                                            <select
                                                className="form-control"
                                                value={form.paymentMethodId}
                                                onChange={(e) =>
                                                    handleChange(
                                                        'paymentMethodId',
                                                        e.target.value,
                                                    )
                                                }
                                            >
                                                <option value="">
                                                    Seleccionar...
                                                </option>
                                                {paymentMethods.map((pm) => (
                                                    <option key={pm.id} value={pm.id}>
                                                        {pm.name}
                                                    </option>
                                                ))}
                                            </select>
                                        </div>
                                    </div>

                                    <div className="form-group">
                                        <label>Descripción del paquete</label>
                                        <input
                                            type="text"
                                            className="form-control"
                                            placeholder="Ej: caja de medicamentos x5"
                                            value={form.descripcionPaquete}
                                            onChange={(e) =>
                                                handleChange(
                                                    'descripcionPaquete',
                                                    e.target.value,
                                                )
                                            }
                                        />
                                    </div>

                                    <hr />

                                    {/* Cobro en entrega */}
                                    <h6 className="mb-3">Cobro en entrega</h6>
                                    <div className="form-group">
                                        <label>Valor del paquete (COP)</label>
                                        <input
                                            type="number"
                                            className="form-control"
                                            placeholder="Ej: 25000"
                                            value={form.valorPaquete}
                                            onChange={(e) =>
                                                handleChange(
                                                    'valorPaquete',
                                                    e.target.value,
                                                )
                                            }
                                        />
                                        <small className="form-text text-muted">
                                            Lo que vale el paquete que manda el
                                            negocio. Solo referencia.
                                        </small>
                                    </div>
                                    <div className="form-group">
                                        <label>Qué cobrar en la puerta</label>
                                        <div className="form-check">
                                            <input
                                                className="form-check-input"
                                                type="radio"
                                                name="tipoCobro"
                                                id="cobro-completo"
                                                value="completo"
                                                checked={
                                                    form.tipoCobro === 'completo'
                                                }
                                                onChange={(e) =>
                                                    handleChange(
                                                        'tipoCobro',
                                                        e.target.value,
                                                    )
                                                }
                                            />
                                            <label
                                                className="form-check-label"
                                                htmlFor="cobro-completo"
                                            >
                                                Cobro completo — paquete + envío
                                            </label>
                                        </div>
                                        <div className="form-check">
                                            <input
                                                className="form-check-input"
                                                type="radio"
                                                name="tipoCobro"
                                                id="cobro-solo-envio"
                                                value="solo_envio"
                                                checked={
                                                    form.tipoCobro ===
                                                    'solo_envio'
                                                }
                                                onChange={(e) =>
                                                    handleChange(
                                                        'tipoCobro',
                                                        e.target.value,
                                                    )
                                                }
                                            />
                                            <label
                                                className="form-check-label"
                                                htmlFor="cobro-solo-envio"
                                            >
                                                Solo envío — el paquete ya está
                                                pagado
                                            </label>
                                        </div>
                                        <div className="form-check">
                                            <input
                                                className="form-check-input"
                                                type="radio"
                                                name="tipoCobro"
                                                id="cobro-pagado"
                                                value="pagado"
                                                checked={
                                                    form.tipoCobro === 'pagado'
                                                }
                                                onChange={(e) =>
                                                    handleChange(
                                                        'tipoCobro',
                                                        e.target.value,
                                                    )
                                                }
                                            />
                                            <label
                                                className="form-check-label"
                                                htmlFor="cobro-pagado"
                                            >
                                                Ya pagado — no cobrar nada
                                            </label>
                                        </div>
                                    </div>

                                    <div className="form-group">
                                        <label>Vueltos (COP) — opcional</label>
                                        <input
                                            type="number"
                                            className="form-control"
                                            placeholder="Ej: 5000"
                                            value={form.vueltos}
                                            onChange={(e) =>
                                                handleChange(
                                                    'vueltos',
                                                    e.target.value,
                                                )
                                            }
                                        />
                                        <small className="form-text text-muted">
                                            Solo si el receptor paga en
                                            efectivo y necesita vueltos.
                                        </small>
                                    </div>

                                    <button
                                        type="submit"
                                        className="btn btn-primary"
                                        disabled={submitting}
                                    >
                                        {submitting ? 'Registrando...' : 'Registrar envío'}
                                    </button>
                                </form>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </Fragment>
    );
};

export default NewEnvio;
